import PortalShell from "../../components/PortalShell";
import { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { Bot, Send, Sparkles } from "lucide-react";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Explain quadratic formula with an example",
  "How do I simplify (x² + 5x + 6) / (x + 2)?",
  "Walk me through SOHCAHTOA",
  "What is the nth term of 3, 7, 11, 15...?",
];

export default function AITutor() {
  const [sessionId] = useState(() => `tutor_${Date.now()}`);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight); }, [messages, loading]);

  const send = async (text) => {
    const message = text ?? input;
    if (!message.trim()) return;
    setMessages(m => [...m, { role: "user", text: message }]);
    setInput(""); setLoading(true);
    try {
      const { data } = await api.post("/ai/tutor", { message, session_id: sessionId });
      setMessages(m => [...m, { role: "ai", text: data.reply }]);
    } catch {
      toast.error("Tutor is temporarily unavailable. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <PortalShell role="student" title="AI Maths Tutor" subtitle="Powered by Claude Sonnet 4.5 · Step-by-step workings, 24/7.">
      <div className="grid md:grid-cols-[1fr_280px] gap-6">
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden flex flex-col h-[70vh]">
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 thin-scroll space-y-4">
            {messages.length === 0 && (
              <div className="text-center text-slate-500 py-12">
                <div className="w-14 h-14 mx-auto rounded-full bg-brand-navy flex items-center justify-center mb-4" style={{color:"#D4AF37"}}><Sparkles/></div>
                <div className="font-serif text-xl brand-navy mb-2">Ask me anything in GCSE Maths</div>
                <p className="text-sm">I'll show every line of working — like an examiner.</p>
              </div>
            )}
            {messages.map((m,i)=>(
              <div key={i} className={`flex ${m.role==="user"?"justify-end":"justify-start"}`} data-testid={`chat-msg-${i}`}>
                <div className={`max-w-[80%] px-4 py-3 rounded-lg text-sm leading-relaxed whitespace-pre-wrap ${m.role==="user"?"bg-brand-navy text-white":"bg-slate-50 brand-navy border border-slate-200"}`}>{m.text}</div>
              </div>
            ))}
            {loading && <div className="flex justify-start"><div className="bg-slate-50 px-4 py-3 rounded-lg text-sm text-slate-500 border border-slate-200 italic">Thinking through workings…</div></div>}
          </div>

          <form onSubmit={e=>{e.preventDefault();send();}} className="border-t border-slate-200 p-4 flex gap-2">
            <input value={input} onChange={e=>setInput(e.target.value)} placeholder="Ask your maths question…" className="flex-1 border border-slate-300 rounded-md px-4 py-3 text-sm" data-testid="tutor-input"/>
            <button type="submit" disabled={loading || !input.trim()} className="btn-gold px-5 rounded-md disabled:opacity-60" data-testid="tutor-send"><Send size={16}/></button>
          </form>
        </div>

        <aside className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-2 mb-3"><Bot size={16} className="gold-text"/><h4 className="font-serif brand-navy">Try asking</h4></div>
            <div className="space-y-2">
              {SUGGESTIONS.map((s,i)=>(
                <button key={i} onClick={()=>send(s)} className="text-left text-xs w-full p-3 rounded-md bg-slate-50 hover:bg-slate-100 border border-slate-200 transition" data-testid={`tutor-suggestion-${i}`}>{s}</button>
              ))}
            </div>
          </div>
          <div className="panel-navy rounded-xl p-5">
            <div className="text-[10px] uppercase tracking-widest brand-gold mb-2">Tip</div>
            <p className="text-sm text-white/80 leading-relaxed">Paste a full question from a past paper. The tutor will solve step-by-step and check your understanding.</p>
          </div>
        </aside>
      </div>
    </PortalShell>
  );
}
